import { FC, ReactNode, useMemo } from "react"

import isUndefined from "lodash-es/isUndefined"
import { Link } from "react-router-dom"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography,
    ViewAsBanner
} from "@/3514/components"
import {
    type TLogoutFunction,
    type TScreenBreakpoints,
    type TUserState,
    useIsFakeParticipant,
    useLogout,
    useScreenBreakpoints,
    useUser
} from "@/3514/hooks"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { buildCssClassName, isUserCoach } from "@/3514/utils"
import ParticipantLayoutHeader from "@/elements/participant-layout-header/ParticipantLayoutHeader"
import UserHeaderDropdown from "@/elements/participant-layout-header/UserHeaderDropdown"
import { type IUseTranslation, type TUseLogoNavigation, useLogoNavigation, useTranslation } from "@/hooks"
import { LocalStorageServiceKeys, StorageService } from "@/services"
import ProductTypeLogo from "@/shared/ProductTypeLogo"
import BackButton from "@/shared/back-btn/BackButton"

import "./styles.scss"

interface IProps {
    header?: ReactNode
    pageTitle?: ReactNode
    withProfileHeader?: boolean
    withoutHeader?: boolean
    headerVariant?: "default" | "gray"
    showBackBtn?: boolean
    showProfilePhoto?: boolean
    isSimpleHeader?: boolean
    showAvatarOnMobile?: boolean
    withFooter?: boolean
    children: ReactNode
    withUserDropdownWithoutMargin?: boolean
    className?: string
    mainClassName?: string
    _withoutLogo?: boolean
    _withNoSidebarSpacingOffset?: boolean
}

const storageService: StorageService = new StorageService()

const SimpleLayout: FC<IProps> = ({
    children,
    header = null,
    pageTitle = null,
    withProfileHeader = false,
    withoutHeader = false,
    headerVariant = "default",
    showBackBtn = false,
    isSimpleHeader,
    showAvatarOnMobile = false,
    withFooter = true,
    showProfilePhoto = false,
    withUserDropdownWithoutMargin = false,
    className = "",
    _withoutLogo = false,
    _withNoSidebarSpacingOffset = false,
    mainClassName = undefined
}) => {
    const { t }: IUseTranslation = useTranslation()

    const { user }: TUserState = useUser()

    const logout: TLogoutFunction = useLogout()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const handleLogout = async e => {
        e.preventDefault()

        void logout({
            keepLocalStorageAuthTokens: isFakeParticipant,
            onComplete: async (): Promise<void> => {
                if (isUserCoach(user) && user.areSubUsersPresent)
                    storageService.setItem<boolean>(LocalStorageServiceKeys.MainUserLoggedOut(), true)
            }
        })
    }

    const { handleLogoNavigation, logoNavigationUrl }: TUseLogoNavigation = useLogoNavigation()

    const navItem = useMemo(() => {
        if (isSimpleHeader) {
            return <ProductTypeLogo width={26} heigth={34} handleLogoClick={handleLogoNavigation} />
        }

        if (withProfileHeader) {
            return (
                <ParticipantLayoutHeader
                    withBurgerMenu={false}
                    header={header}
                    fullWidth
                    showLogo={false}
                    _withoutLogo={_withoutLogo}
                />
            )
        }

        if ((showProfilePhoto && !isMobile) || (showProfilePhoto && showAvatarOnMobile)) {
            return (
                <UserHeaderDropdown
                    handleLogout={handleLogout}
                    user={user}
                    className={buildCssClassName("simple-layout-user-dropdown", {
                        "ml-auto": !withProfileHeader && !withUserDropdownWithoutMargin
                    })}
                />
            )
        }

        if (!showProfilePhoto && (!withProfileHeader || isMobile)) {
            return (
                <div>
                    <a href="#" className="text-dark" onClick={handleLogout}>
                        {t("common.signOut")}
                    </a>
                </div>
            )
        }
    }, [withProfileHeader, showProfilePhoto, showAvatarOnMobile, isSimpleHeader, isMobile])

    const appState: IAppState = useAppSelector()

    return (
        <div className={buildCssClassName("wrapper flex-column", { [className]: className })}>
            {isFakeParticipant && <ViewAsBanner />}
            <header className={buildCssClassName("header-sticky", { "bg-simple-gray": headerVariant === "gray" })}>
                <div className="container">
                    <nav className={buildCssClassName("navbar navbar-expand-lg navbar-simple")}>
                        {!withProfileHeader && !withoutHeader && (
                            <Link to={logoNavigationUrl} className="home-link d-none-mobile">
                                <ProductTypeLogo width={26} heigth={34} />
                            </Link>
                        )}
                        {header && <span className="header-center header-center-mobile mx-auto">{header}</span>}
                        {navItem}
                    </nav>
                </div>
            </header>
            <main className={buildCssClassName("main w-100", { [mainClassName]: mainClassName })}>
                <div
                    className="container"
                    style={{
                        marginLeft:
                            breakpoints.xs || breakpoints.sm || isUndefined(user) || _withNoSidebarSpacingOffset
                                ? 0
                                : appState?.isOpenSidebar
                                  ? 250
                                  : 80,
                        marginRight:
                            breakpoints.xs || breakpoints.sm || isUndefined(user) || _withNoSidebarSpacingOffset
                                ? 0
                                : undefined
                    }}
                >
                    {pageTitle && (
                        <div className="page-title">
                            <Typography
                                text={pageTitle}
                                className="mb-[6px]"
                                alignment={ETypographyAlignment.Center}
                                tag={ETypographyTag.Heading1}
                                color={ETypographyColor.Black}
                                weight={ETypographyWeight.Medium}
                                size={ETypographySize.Heading1}
                                family={ETypographyFamily.DmSerif}
                            />
                        </div>
                    )}
                    {children}
                </div>
            </main>
            {withFooter && <footer className="footer" />}
            {showBackBtn && <BackButton className="text-accentOld absolute top-[75px]" />}
        </div>
    )
}

export default SimpleLayout
